import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { DailyReport, WEATHER_CONDITIONS } from '../../objects/daily-report';
import { DailyReportService } from '../../Services/daily-report.service';
import { Project } from '../../objects/Project';

@Component({
  selector: 'app-project-daily-report',
  templateUrl: './project-daily-report.component.html',
  styleUrls: ['./project-daily-report.component.css']
})
export class ProjectDailyReportComponent implements OnInit, OnDestroy {
  @Input() project?: Project;

  reports: DailyReport[] = [];
  editingReport: DailyReport | null = null;
  showForm = false;
  isLoading = false;
  weatherOptions = WEATHER_CONDITIONS;

  form: FormGroup;
  private subs: Subscription[] = [];

  constructor(
    private reportService: DailyReportService,
    private snackBar: MatSnackBar
  ) {
    this.form = this.buildForm();
  }

  ngOnInit(): void {
    this.load();
    this.subs.push(
      this.reportService.reports$.subscribe(r => {
        this.reports = [...r].sort((a, b) =>
          new Date(b.reportDate).getTime() - new Date(a.reportDate).getTime()
        );
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  load(): void {
    if (!this.project?.id) { return; }
    this.isLoading = true;
    const sub = this.reportService.getByProject(this.project.id).subscribe({
      next: () => (this.isLoading = false),
      error: () => (this.isLoading = false)
    });
    this.subs.push(sub);
  }

  openAdd(): void {
    this.editingReport = null;
    this.form.reset({
      reportDate: new Date().toISOString().split('T')[0],
      weatherConditions: 'Clear',
      workersOnSite: 0,
      hoursWorked: 8,
      requiresInspection: false
    });
    this.showForm = true;
  }

  openEdit(report: DailyReport): void {
    this.editingReport = report;
    this.form.patchValue(report);
    this.showForm = true;
  }

  cancel(): void {
    this.showForm = false;
    this.editingReport = null;
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const value: Partial<DailyReport> = {
      ...this.form.value,
      projectId: this.project?.id ?? 0
    };

    if (this.editingReport?.id) {
      const sub = this.reportService.update(this.editingReport.id, value as DailyReport).subscribe({
        next: () => {
          this.snackBar.open('Report updated', '', { duration: 2500 });
          this.cancel();
        }
      });
      this.subs.push(sub);
    } else {
      const sub = this.reportService.create(value).subscribe({
        next: () => {
          this.snackBar.open('Report saved', '', { duration: 2500 });
          this.cancel();
        }
      });
      this.subs.push(sub);
    }
  }

  remove(report: DailyReport): void {
    if (!confirm(`Delete report for ${report.reportDate}?`)) { return; }
    if (!report.id) { return; }
    const sub = this.reportService.delete(report.id).subscribe({
      next: () => this.snackBar.open('Report deleted', '', { duration: 2500 })
    });
    this.subs.push(sub);
  }

  hasInspection(report: DailyReport): boolean {
    return report.requiresInspection;
  }

  hasIssues(report: DailyReport): boolean {
    return !!(report.issuesEncountered?.trim() || report.safetyIncidents?.trim());
  }

  private buildForm(): FormGroup {
    return new FormGroup({
      reportDate: new FormControl('', Validators.required),
      foremanName: new FormControl('', Validators.required),
      weatherConditions: new FormControl('Clear'),
      temperature: new FormControl(''),
      workersOnSite: new FormControl(0, [Validators.required, Validators.min(0)]),
      hoursWorked: new FormControl(8, [Validators.required, Validators.min(0), Validators.max(24)]),
      workCompleted: new FormControl('', Validators.required),
      materialsUsed: new FormControl(''),
      issuesEncountered: new FormControl(''),
      safetyIncidents: new FormControl(''),
      requiresInspection: new FormControl(false),
      inspectionDetails: new FormControl(''),
      delaysOrSetbacks: new FormControl(''),
      nextDayPlan: new FormControl('')
    });
  }
}
